import { useState } from "react";
import { X, ArrowLeft, Plus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, img, errMsg } from "@/lib/api";
import { UploadButton } from "./ImageUploader";

const EMPTY = { name: "", price: 0, category: "Outerwear", description: "", details: "", images: [], colours: [], sizes: ["XS", "S", "M", "L", "XL"], stock: {}, is_featured: false, is_new: true, active: true, preorder: false, preorder_note: "" };
const Label = ({ children }) => <span className="eyebrow text-[var(--mm-muted)] block mb-1">{children}</span>;

const ImagesField = ({ images, setImages }) => {
  const [url, setUrl] = useState("");
  const move = (i) => { const n = [...images]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; setImages(n); };
  return (
    <div>
      <Label>Images (first image is the main photo)</Label>
      <div className="flex flex-wrap gap-2 my-3">
        {images.map((src, i) => (
          <div key={src + i} className="relative w-20 aspect-[3/4] bg-[var(--mm-sand)] group">
            <img src={img(src)} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => setImages(images.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-[var(--mm-cream)] p-0.5" aria-label="Remove image" data-testid={`editor-remove-image-${i}`}><X size={12} /></button>
            {i > 0 && <button type="button" onClick={() => move(i)} className="absolute bottom-1 left-1 bg-[var(--mm-cream)] p-0.5" aria-label="Move left"><ArrowLeft size={12} /></button>}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-3 items-center">
        <UploadButton multiple testId="editor-upload-images-button" label="Upload photos" onUploaded={(u) => setImages((prev) => [...prev, u])} />
        <input data-testid="editor-image-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="…or paste an image URL" className="field-mm flex-1 min-w-[180px] py-2" />
        <button type="button" data-testid="editor-add-image-url-button" onClick={() => { if (url) { setImages((p) => [...p, url]); setUrl(""); } }} className="eyebrow">Add</button>
      </div>
    </div>
  );
};

const ColoursField = ({ colours, setColours }) => (
  <div>
    <Label>Colours</Label>
    {colours.map((c, i) => (
      <div key={i} className="flex gap-3 items-center mb-2">
        <input type="color" value={c.hex} onChange={(e) => setColours(colours.map((x, j) => (j === i ? { ...x, hex: e.target.value } : x)))} className="w-9 h-9 border-0 bg-transparent" />
        <input data-testid={`editor-colour-name-${i}`} value={c.name} onChange={(e) => setColours(colours.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} placeholder="Colour name" className="field-mm py-2 flex-1" />
        <button type="button" onClick={() => setColours(colours.filter((_, j) => j !== i))} aria-label="Remove colour"><X size={14} /></button>
      </div>
    ))}
    <button type="button" data-testid="editor-add-colour-button" onClick={() => setColours([...colours, { name: "", hex: "#4A2E24" }])} className="eyebrow inline-flex items-center gap-1 mt-1"><Plus size={12} /> Add colour</button>
  </div>
);

export default function ProductEditor({ product, onClose, onSaved }) {
  const [f, setF] = useState({ ...EMPTY, ...product });
  const [sizesText, setSizesText] = useState((product.sizes || EMPTY.sizes).join(", "));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: typeof v === "function" ? v(p[k]) : v }));
  const sizes = sizesText.split(",").map((s) => s.trim()).filter(Boolean);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const stock = Object.fromEntries(sizes.map((s) => [s, Number(f.stock?.[s] || 0)]));
    const body = { ...f, sizes, stock, price: Number(f.price), colours: f.colours.filter((c) => c.name) };
    delete body.id; delete body.slug; delete body.created_at;
    try {
      if (product.id) await api.put(`/admin/products/${product.id}`, body); else await api.post("/admin/products", body);
      toast.success("Product saved");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="product-editor-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[var(--mm-cream)] rounded-none">
        <DialogTitle className="font-serif-mm text-4xl font-normal">{product.id ? "Edit product" : "New product"}</DialogTitle>
        <DialogDescription className="sr-only">Edit product details</DialogDescription>
        <form onSubmit={save} className="space-y-6 mt-2">
          <div className="grid sm:grid-cols-3 gap-5">
            <label className="sm:col-span-3"><Label>Name</Label><input data-testid="editor-name-input" required value={f.name} onChange={(e) => set("name", e.target.value)} className="field-mm" /></label>
            <label><Label>Price (£)</Label><input data-testid="editor-price-input" type="number" step="0.01" min="0" required value={f.price} onChange={(e) => set("price", e.target.value)} className="field-mm" /></label>
            <label className="sm:col-span-2"><Label>Category</Label><input data-testid="editor-category-input" required value={f.category} onChange={(e) => set("category", e.target.value)} className="field-mm" /></label>
          </div>
          <label className="block"><Label>Description</Label><textarea data-testid="editor-description-input" rows={3} value={f.description} onChange={(e) => set("description", e.target.value)} className="field-mm resize-none" /></label>
          <label className="block"><Label>Details & care (one per line)</Label><textarea data-testid="editor-details-input" rows={3} value={f.details} onChange={(e) => set("details", e.target.value)} className="field-mm resize-none" /></label>
          <label className="block"><Label>Square payment link (e.g. https://square.link/u/...)</Label><input data-testid="editor-square-link-input" type="url" value={f.square_link || ""} onChange={(e) => set("square_link", e.target.value.trim())} placeholder="https://square.link/u/..." className="field-mm" /></label>
          {f.preorder && <label className="block"><Label>Pre-order note (e.g. Ships from 1 December)</Label><input data-testid="editor-preorder-note-input" value={f.preorder_note || ""} onChange={(e) => set("preorder_note", e.target.value)} className="field-mm" /></label>}
          <ImagesField images={f.images} setImages={(v) => set("images", v)} />
          <ColoursField colours={f.colours} setColours={(v) => set("colours", v)} />
          <label className="block"><Label>Sizes (comma separated)</Label><input data-testid="editor-sizes-input" value={sizesText} onChange={(e) => setSizesText(e.target.value)} className="field-mm" /></label>
          <div>
            <Label>Stock per size</Label>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mt-2">
              {sizes.map((s) => (
                <label key={s} className="text-xs">{s}<input data-testid={`editor-stock-${s}`} type="number" min="0" value={f.stock?.[s] ?? 0} onChange={(e) => set("stock", (st) => ({ ...st, [s]: e.target.value }))} className="field-mm py-1.5" /></label>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-8">
            {[["active", "Visible in shop"], ["is_new", "New arrival"], ["is_featured", "Featured on homepage"], ["preorder", "Pre-order (turn off on launch day)"]].map(([k, l]) => (
              <label key={k} className="flex items-center gap-3 text-sm"><Switch data-testid={`editor-${k}-switch`} checked={!!f[k]} onCheckedChange={(v) => set(k, v)} />{l}</label>
            ))}
          </div>
          <div className="flex gap-3 pt-2">
            <button data-testid="editor-save-button" disabled={saving} className="btn-mm">{saving ? "Saving…" : "Save product"}</button>
            <button type="button" onClick={onClose} className="btn-outline-mm">Cancel</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
